import React from "react";
import { Lock, ShieldCheck } from "lucide-react";
import { Input } from "../ui/input";
import { Field, CardBadge } from "./checkoutUtils";

export const PaymentSection = ({
  form,
  set,
  cardContainerRef,
  squareReady,
  squareError,
  applePayAvailable,
  googlePayAvailable,
  submitting,
  onWalletPay,
}) => (
  <section className="bg-white rounded-3xl border border-slate-200 p-7 shadow-sm">
    <div className="flex items-center justify-between mb-5">
      <div>
        <h2 className="font-display text-xl font-bold text-knight-navy mb-1">Payment Method</h2>
        <p className="text-xs text-knight-navy/55 flex items-center gap-1"><Lock size={11} /> Encrypted &amp; secure</p>
      </div>
      <div className="flex items-center gap-1">
        <CardBadge label="VISA" />
        <CardBadge label="MC" />
        <CardBadge label="AMEX" />
        <CardBadge label="DISC" />
      </div>
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Cardholder Name *" wide><Input value={form.cardholder} onChange={set("cardholder")} placeholder="Jane M. Doe" data-testid="cardholder-name-input" /></Field>
      <Field label="Card *" wide>
        <div
          id="square-card-container"
          ref={cardContainerRef}
          className="min-h-[58px] rounded-lg border border-slate-200 px-3 py-2 bg-white focus-within:border-knight-navy transition-colors"
          data-testid="square-card-container"
        />
        {squareError && (
          <p className="text-xs text-red-600 mt-2">{squareError}</p>
        )}
        {!squareReady && !squareError && (
          <p className="text-xs text-knight-navy/60 mt-2 flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full border-2 border-knight-navy/30 border-t-knight-navy animate-spin" />
            Loading secure card field&hellip;
          </p>
        )}
      </Field>
    </div>

    {/* Express Checkout — Apple Pay & Google Pay */}
    {(applePayAvailable || googlePayAvailable) && (
      <div className="mt-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-[10px] font-bold tracking-[0.22em] text-knight-navy/55 uppercase">Express Checkout</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {applePayAvailable && (
            <button
              type="button"
              onClick={() => onWalletPay("apple", "Apple Pay")}
              disabled={submitting}
              data-testid="apple-pay-button"
              className="h-12 rounded-lg bg-black hover:bg-neutral-900 text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              aria-label="Pay with Apple Pay"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.05 12.04c-.03-2.96 2.42-4.39 2.53-4.46-1.38-2.02-3.53-2.3-4.3-2.33-1.83-.19-3.57 1.08-4.5 1.08-.93 0-2.36-1.05-3.88-1.02-2 .03-3.84 1.16-4.87 2.95-2.08 3.6-.53 8.93 1.5 11.86.99 1.43 2.17 3.04 3.7 2.99 1.49-.06 2.05-.96 3.85-.96s2.31.96 3.88.93c1.6-.03 2.61-1.46 3.59-2.9 1.13-1.66 1.6-3.26 1.63-3.35-.04-.02-3.13-1.2-3.16-4.77Zm-2.95-8.77c.82-1 1.37-2.4 1.22-3.79-1.17.05-2.6.78-3.45 1.77-.76.88-1.43 2.3-1.25 3.66 1.3.1 2.65-.66 3.48-1.64Z"/>
              </svg>
              Pay
            </button>
          )}
          {googlePayAvailable && (
            <div
              id="google-pay-button"
              data-testid="google-pay-button"
              className="h-12 rounded-lg overflow-hidden"
            />
          )}
        </div>
        <p className="text-[11px] text-knight-navy/55 mt-3">Or pay with card below:</p>
      </div>
    )}

    <div className="mt-4 flex items-center gap-2 text-xs text-knight-navy/55">
      <ShieldCheck size={14} className="text-emerald-600" /> Your card is tokenized by Square. We never see or store full card details.
    </div>
  </section>
);
